/**
 * ══════════════════════════════════════════════════════════════
 * NETUNO — Tipos para Diagnóstico Analítico (Root Cause)
 * ══════════════════════════════════════════════════════════════
 *
 * Modelo padrão para análises de causa raiz geradas
 * automaticamente a partir dos insights detectados.
 *
 * Uso: import { RootCauseAnalysis } from '@/types/diagnostics';
 * ══════════════════════════════════════════════════════════════
 */

import type { InsightSeverity } from '@/types/insights';

// ─── Probable Cause ─────────────────────────────────────────

export interface ProbableCause {
  /** Unique cause ID */
  id: string;
  /** Short cause label */
  label: string;
  /** Detailed explanation */
  description: string;
  /** Confidence score 0-100 */
  confidence: number;
  /** Supporting evidence items */
  evidence: CauseEvidence[];
  /** Category of cause */
  category: CauseCategory;
}

export type CauseCategory =
  | 'delay_pattern'
  | 'cost_overrun'
  | 'quality_issues'
  | 'resource_constraint'
  | 'scope_change'
  | 'external_factor'
  | 'concentration_risk'
  | 'trend_systemic';

export interface CauseEvidence {
  /** Evidence type */
  type: 'metric' | 'pattern' | 'correlation' | 'threshold';
  /** Short label */
  label: string;
  /** Value or description */
  value: string;
  /** Related metric key */
  metric?: string;
}

// ─── Affected Entity ────────────────────────────────────────

export interface AffectedEntity {
  type: 'contract' | 'uo' | 'hub' | 'cluster' | 'portfolio';
  id: string;
  name: string;
  /** Impact level */
  impact: 'high' | 'medium' | 'low';
  /** Route for drill-down */
  drillDownRoute?: string;
}

// ─── Recommended Action ─────────────────────────────────────

export interface DiagnosticAction {
  /** Action ID */
  id: string;
  /** Short action title */
  title: string;
  /** Detailed description */
  description: string;
  /** Priority level */
  priority: 'immediate' | 'short_term' | 'medium_term';
  /** Investigation route */
  drillDownRoute?: string;
}

// ─── Root Cause Analysis ────────────────────────────────────

export interface RootCauseAnalysis {
  /** Unique analysis ID */
  id: string;
  /** Related insight ID that triggered this analysis */
  relatedInsightId: string;
  /** Summary of the diagnosis */
  summary: string;
  /** Overall severity inherited from insight */
  severity: InsightSeverity;
  /** Probable causes sorted by confidence */
  probableCauses: ProbableCause[];
  /** Overall confidence score 0-100 */
  confidenceScore: number;
  /** Entities affected by the root cause */
  affectedEntities: AffectedEntity[];
  /** Recommended investigative/corrective actions */
  recommendedActions: DiagnosticAction[];
  /** When the diagnosis was generated */
  generatedAt: string;
  /** Additional metadata */
  metadata?: Record<string, unknown>;
}

// ─── Diagnostic Context ─────────────────────────────────────

export interface DiagnosticContext {
  /** Contract ranking data */
  contracts?: Array<{
    contract_id: string;
    contrato_nome: string;
    score_criticidade: number;
    nivel_criticidade: string;
    prob_atraso_mc: number | null;
    tendencia: string | null;
    total_ocorrencias: number | null;
    cluster_nome?: string;
  }>;
  /** UO data */
  uos?: Array<{
    uo_id: string;
    uo_nome: string;
    nivel_risco: string;
    score_medio: number;
    total_contratos: number;
  }>;
  /** Portfolio summary */
  portfolio?: {
    total: number;
    criticos: number;
    altos: number;
    moderados: number;
    baixos: number;
    piorando: number;
  };
}

// ─── Helpers ────────────────────────────────────────────────

export const CAUSE_CATEGORY_LABELS: Record<CauseCategory, string> = {
  delay_pattern: 'Padrão de Atraso',
  cost_overrun: 'Estouro de Custo',
  quality_issues: 'Problemas de Qualidade',
  resource_constraint: 'Restrição de Recursos',
  scope_change: 'Alteração de Escopo',
  external_factor: 'Fator Externo',
  concentration_risk: 'Concentração de Risco',
  trend_systemic: 'Tendência Sistêmica',
};

export const ACTION_PRIORITY_LABELS: Record<DiagnosticAction['priority'], string> = {
  immediate: 'Imediata',
  short_term: 'Curto Prazo',
  medium_term: 'Médio Prazo',
};
